import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ConfirmationService, MenuItem, MessageService, SharedModule } from 'primeng/api';
import { Button } from 'primeng/button';
import { IconField } from 'primeng/iconfield';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputIcon } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { Menu } from 'primeng/menu';
import { TableModule } from 'primeng/table';
import { Tag } from 'primeng/tag';
import { Tooltip } from 'primeng/tooltip';
import { debounceTime, Subject } from 'rxjs';
import BodyType from '../../../../types/body-type/BodyType';
import Brand from '../../../../types/brand/Brand';
import CarClass from '../../../../types/car-class/CarClass';
import Detail from '../../../../types/detail/Detail';
import SampleCategory from '../../../../types/sample-category/SampleCategory';
import SampleType from '../../../../types/sample-type/SampleType';
import { FieldOfUse } from '../../../../types/sample/FieldOfUse';
import Sample from '../../../../types/sample/Sample';
import SampleFormat from '../../../../types/sample/SampleFormat';
import { SampleState } from '../../../../types/sample/SampleState';
import SampleWriteDTO from '../../../../types/sample/SampleWriteDTO';
import Page from '../../../../types/shared/Page';
import { Role } from '../../../../types/shared/Role';
import SelectOption from '../../../../types/shared/SelectOption';
import { IsMaintainerDirective } from '../../directives/is-maintainer.directive';
import { EnumLabelPipe } from '../../pipes/enum-label.pipe';
import { FormatDateTime } from '../../pipes/format-date-time';
import { FieldOfUseSeverityPipe } from '../../pipes/sample-field-of-use-severity.pipe';
import { SampleStateSeverityPipe } from '../../pipes/sample-state-severity.pipe';
import { AuthService } from '../../services/auth.service';
import { BodyTypeService } from '../../services/body-type.service';
import { BrandService } from '../../services/brand.service';
import { CarClassService } from '../../services/car-class.service';
import { DetailService } from '../../services/detail.service';
import { ExportService } from '../../services/export.service';
import { SampleHelperService } from '../../services/helper/sample-helper.service';
import { SampleCategoryService } from '../../services/sample-category.service';
import { SampleProcessLogService } from '../../services/sample-process-log.service';
import { SampleTypeService } from '../../services/sample-type.service';
import { SampleService } from '../../services/sample.service';
import { UtilsService } from '../../services/utils.service';
import { PageHeaderComponent } from '../shared/page-header/page-header.component';
import {
    ChangeSampleStateDialogComponent
} from '../shared/samples/change-sample-state-dialog/change-sample-state-dialog.component';
import { CreateSampleDialogComponent } from '../shared/samples/create-sample-dialog/create-sample-dialog.component';
import { CustomExportDialogComponent } from '../shared/samples/custom-export-dialog/custom-export-dialog.component';
import { FilterSamplesDialogComponent } from '../shared/samples/filter-samples-dialog/filter-samples-dialog.component';
import { SampleProcessComponent } from '../shared/samples/sample-process/sample-process.component';
import { ToggleOnHoldDialogComponent } from '../shared/samples/toggle-on-hold-dialog/toggle-on-hold-dialog.component';
import { UpdateSampleDialogComponent } from '../shared/samples/update-sample-dialog/update-sample-dialog.component';

@Component({
    selector: 'app-samples',
    templateUrl: './samples.component.html',
    styleUrls: [ './samples.component.scss' ],
    imports: [
        Button,
        TableModule,
        InputIcon,
        InputText,
        PageHeaderComponent,
        IconField,
        FormsModule,
        SharedModule,
        CreateSampleDialogComponent,
        UpdateSampleDialogComponent,
        Tag,
        SampleStateSeverityPipe,
        FieldOfUseSeverityPipe,
        EnumLabelPipe,
        IftaLabelModule,
        FilterSamplesDialogComponent,
        ChangeSampleStateDialogComponent,
        SampleProcessComponent,
        FormatDateTime,
        Menu,
        IsMaintainerDirective,
        Tooltip,
        ToggleOnHoldDialogComponent,
        CustomExportDialogComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SamplesComponent implements OnInit {
    public rowActionItems: MenuItem[] = [];

    public search = '';
    public selectedFieldsOfUse: FieldOfUse[] = [];
    public selectedSampleTypes: SampleType[] = [];
    public selectedSampleStates: SampleState[] = [];
    public selectedSampleCategories: SampleCategory[] = [];
    public selectedSampleDetails: Detail[] = [];
    public selectedFormats: SampleFormat[] = [];

    public samples: Sample[] = [];
    public totalRecords = 0;
    public rows = 5;
    public currentPage = 0;
    public sortField = 'lastActivity';
    public sortOrder = -1;
    public loading = false;
    public selectedSample: SampleWriteDTO = new SampleWriteDTO({});
    public sampleToCopy: SampleWriteDTO | null = null;

    private searchSubject = new Subject<string>();

    public fieldOfUseOptions: SelectOption[] = [];
    public sampleTypeOptions: SampleType[] = [];
    public sampleStateOptions: SelectOption[] = [];
    public sampleCategoryOptions: SampleCategory[] = [];
    public sampleDetailsOptions: Detail[] = [];
    public sampleFormatOptions: SelectOption[] = [
        { label: 'Standard', value: 'STANDARD' },
        { label: 'G-Class Dedicated', value: 'G_CLASS' },
        { label: 'Maybach Dedicated', value: 'MAYBACH' },
    ];
    public brandOptions: Brand[] = [];
    public carClassOptions: CarClass[] = [];
    public bodyTypeOptions: BodyType[] = [];

    // Dialogs
    public displayCreateSampleDialog: boolean = false;
    public displayUpdateSampleDialog: boolean = false;
    public filterDialogVisible: boolean = false;
    public showOnHoldDialog: boolean = false;
    public displayCustomExportDialog: boolean = false;

    public displayChangeStateDialog = false;
    public changeStateDialogHeader = '';
    public changeStateDialogMessage = '';
    public changeStateDialogNewSampleState: SampleState = SampleState.CREATED;
    public changeStateDialogSampleId: number = 0;
    public selectedSampleObject: Sample | null = null;

    constructor(private sampleService: SampleService,
                private sampleHelperService: SampleHelperService,
                private sampleProcessLogService: SampleProcessLogService,
                private messageService: MessageService,
                private authService: AuthService,
                private utilsService: UtilsService,
                private exportService: ExportService,
                private router: Router,
                private confirmationService: ConfirmationService,
                private sampleTypeService: SampleTypeService,
                private sampleCategoryService: SampleCategoryService,
                private detailsService: DetailService,
                private brandService: BrandService,
                private carClassService: CarClassService,
                private bodyTypeService: BodyTypeService,
                private cdr: ChangeDetectorRef) {
        this.fieldOfUseOptions = this.utilsService.createDropdownOptions(FieldOfUse);
        this.sampleStateOptions = this.utilsService.createDropdownOptions(SampleState);
        this.loadSampleTypes();
        this.loadSampleCategories();
        this.loadDetails();
        this.loadBrands();
        this.loadCarClasses();
        this.loadBodyTypes();
    }

    public ngOnInit(): void {
        this.searchSubject.pipe(debounceTime(300)).subscribe(() => {
            this.currentPage = 0;
            this.loadSamples();
        });

        this.restoreFiltersFromSession();
    }

    public hasActiveFilters(): boolean {
        return this.selectedFieldsOfUse.length > 0 ||
            this.selectedSampleTypes.length > 0 ||
            this.selectedSampleStates.length > 0 ||
            this.selectedSampleCategories.length > 0 ||
            this.selectedSampleDetails.length > 0 ||
            this.selectedFormats.length > 0;
    }

    public displayFilterDialog(): void {
        this.filterDialogVisible = true;
    }

    public openCustomExportDialog(): void {
        this.displayCustomExportDialog = true;
        this.cdr.markForCheck();
    }

    public loadSampleTypes(): void {
        this.sampleTypeService.getAllSampleTypes().then((sampleTypes: SampleType[]) => {
            this.sampleTypeOptions = sampleTypes;
            this.cdr.markForCheck();
        }).catch((error: any) => {
            console.error('Error loading sample types:', error);
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: error.message,
                life: 3000
            });
        });
    }

    public loadSampleCategories(): void {
        this.sampleCategoryService.getAllSampleCategories().then((sampleCategories: SampleCategory[]) => {
            this.sampleCategoryOptions = sampleCategories;
            this.cdr.markForCheck();
        }).catch((error: any) => {
            console.error('Error loading sample categories:', error);
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: error.message,
                life: 3000
            });
        });
    }

    public loadDetails(): void {
        this.detailsService.getAllDetails().then((details: Detail[]) => {
            this.sampleDetailsOptions = details;
            this.cdr.markForCheck();
        }).catch((error: any) => {
            console.error('Error loading details:', error);
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: error.message,
                life: 3000
            });
        });
    }

    public loadBrands(): void {
        this.brandService.getAllBrands().then((brands: Brand[]) => {
            this.brandOptions = brands;
        }).catch((error: any) => {
            console.error('Error loading brands:', error);
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: error.message,
                life: 3000
            });
        });
    }

    public loadCarClasses(): void {
        this.carClassService.getAllCarClasses().then((carClasses: CarClass[]) => {
            this.carClassOptions = carClasses;
        }).catch((error: any) => {
            console.error('Error loading car classes:', error);
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: error.message,
                life: 3000
            });
        });
    }

    public loadBodyTypes(): void {
        this.bodyTypeService.getAllBodyTypes().then((bodyTypes: BodyType[]) => {
            this.bodyTypeOptions = bodyTypes;
        }).catch((error: any) => {
            console.error('Error loading body types:', error);
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: error.message,
                life: 3000
            });
        });
    }

    public loadSamples(): void {
        this.loading = true;

        const size = this.rows;
        const search = this.search || '';
        const fieldsOfUse: FieldOfUse[] | undefined = this.selectedFieldsOfUse && this.selectedFieldsOfUse.length > 0 ?
            this.selectedFieldsOfUse :
            undefined;

        const sampleStates: SampleState[] | undefined = this.selectedSampleStates && this.selectedSampleStates.length >
        0 ?
            this.selectedSampleStates : undefined;

        const sampleCategories: number[] | undefined = this.selectedSampleCategories &&
        this.selectedSampleCategories.length > 0 ?
            this.selectedSampleCategories.map(category => category.id) : undefined;

        const sampleTypes: number[] | undefined = this.selectedSampleTypes && this.selectedSampleTypes.length > 0 ?
            this.selectedSampleTypes.map(type => type.id) : undefined;

        const sampleDetails: number[] | undefined = this.selectedSampleDetails && this.selectedSampleDetails.length >
        0 ?
            this.selectedSampleDetails.map(detail => detail.id) : undefined;

        const formats: SampleFormat[] | undefined = this.selectedFormats && this.selectedFormats.length > 0 ?
            this.selectedFormats : undefined;

        this.sampleService
            .getSamples(
                search, this.currentPage, size, undefined, fieldsOfUse, sampleTypes, sampleStates, sampleCategories,
                sampleDetails, formats, undefined,
                this.sortField, this.sortOrder
            )
            .then((result: Page<Sample>) => {
                let samples = result.content as Sample[];
                samples.forEach(sample => {
                    sample.detailNames = this.getDetailNames(sample);
                });
                this.samples = samples;

                this.totalRecords = result.page.totalElements;
                this.cdr.markForCheck();
            })
            .catch((error: any) => {
                console.error('Error loading samples:', error);
            })
            .finally(() => {
                this.loading = false;
            });
    }

    public onFiltersApplied($event: {
        fieldOfUse: [],
        sampleType: [],
        sampleState: [],
        sampleCategory: [],
        sampleDetails: []
        format: []
    }) {
        this.selectedFieldsOfUse = $event.fieldOfUse;
        this.selectedSampleTypes = $event.sampleType;
        this.selectedSampleStates = $event.sampleState;
        this.selectedSampleCategories = $event.sampleCategory;
        this.selectedSampleDetails = $event.sampleDetails;
        this.selectedFormats = $event.format;

        // Save to sessionStorage
        const filtersToStore = {
            fieldOfUse: this.selectedFieldsOfUse,
            sampleType: this.selectedSampleTypes,
            sampleState: this.selectedSampleStates,
            sampleCategory: this.selectedSampleCategories,
            sampleDetails: this.selectedSampleDetails,
            format: this.selectedFormats,
            search: this.search

        };
        sessionStorage.setItem('sample-filters', JSON.stringify(filtersToStore));

        this.filterDialogVisible = false;
        this.loadSamples();

    }

    public onPageChange(event: any): void {
        this.currentPage = Math.floor(event.first / event.rows);
        this.rows = event.rows;
        this.sortField = event.sortField;
        this.sortOrder = event.sortOrder;
        this.loadSamples();
    }

    public editSample(sample: Sample): void {
        this.selectedSample = new SampleWriteDTO(sample);
        this.selectedSample.manufacturerId = sample.manufacturer?.id;
        this.displayUpdateSampleDialog = true;
    }

    public publishSample(sample: Sample): void {
        this.changeStateDialogSampleId = sample.id;
        this.changeStateDialogHeader = 'Publish Sample';
        this.changeStateDialogMessage =
            'Are you sure you want to publish this sample? This will send an email to the producer and the R&D department.';
        this.changeStateDialogNewSampleState = SampleState.PUBLISHED;
        this.displayChangeStateDialog = true;
    }

    public requestApproval(sample: Sample): void {
        this.changeStateDialogSampleId = sample.id;
        this.changeStateDialogHeader = 'Request Approval';
        this.changeStateDialogMessage =
            'Are you sure you want to request approval for this sample? This will send an email to the sample reviewer.';
        this.changeStateDialogNewSampleState = SampleState.APPROVAL_REQUESTED;
        this.displayChangeStateDialog = true;
    }

    public approveSampleByReviewer(sample: Sample): void {
        this.changeStateDialogSampleId = sample.id;
        this.changeStateDialogHeader = 'Approve Sample';
        this.changeStateDialogMessage =
            'Are you sure you want to approve this sample? This will send an email to the process owner.';
        this.changeStateDialogNewSampleState = SampleState.APPROVED_BY_REVIEWER;
        this.displayChangeStateDialog = true;
    }

    public approveSampleByProcessOwner(sample: Sample): void {
        this.changeStateDialogSampleId = sample.id;
        this.changeStateDialogHeader = 'Set Active';
        this.changeStateDialogMessage = 'Are you sure you want to set this sample active?';
        this.changeStateDialogNewSampleState = SampleState.ACTIVE;
        this.displayChangeStateDialog = true;
    }

    public setInProduction(sample: Sample): void {
        this.changeStateDialogSampleId = sample.id;
        this.changeStateDialogHeader = 'Set In Production';
        this.changeStateDialogMessage = 'Are you sure you want to set this sample to in production?';
        this.changeStateDialogNewSampleState = SampleState.IN_PRODUCTION;
        this.displayChangeStateDialog = true;
    }

    public requestChangesByReviewer(sample: Sample): void {
        this.changeStateDialogSampleId = sample.id;
        this.changeStateDialogHeader = 'Request Changes';
        this.changeStateDialogMessage =
            'Are you sure you want to request changes for this sample? This will send an email to the producer';
        this.changeStateDialogNewSampleState = SampleState.CHANGE_REQUESTED;
        this.displayChangeStateDialog = true;
    }

    public requestMasterSample(sample: Sample): void {
        this.confirmationService.confirm({
            header: 'Request Master Sample',
            message: 'This will place an order for a physical Master Sample and comes with additional costs. For details about the exact costs, please reach out to RD.\n' +
                'Do you want to place the order?',
            accept: () => {
                this.sampleService.requestMasterSample(sample.id).then(() => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Master Sample Requested',
                        detail: 'Master sample requested successfully',
                        life: 3000,
                    });
                    this.loadSamples();
                }).catch((error: any) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: error.message,
                        life: 3000,
                    });
                });
            },
        });
    }

    public deleteSample(sample: Sample): void {
        this.sampleHelperService.deleteSample(sample, () => {
            this.loadSamples();
        });
    }

    public resendLatestMail(sample: Sample): void {
        this.confirmationService.confirm({
            header: 'Resend Mail',
            message: 'Are you sure you want to resend the latest mail for this sample?',
            accept: () => {
                this.sampleProcessLogService.resendLatestMail(sample.id).then(() => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Mail Resent',
                        detail: 'Mail resent successfully',
                        life: 3000,
                    });
                }).catch((error: any) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: error.message,
                        life: 3000,
                    });
                });
            },
        });
    }

    public showPublishSampleButton(sample: Sample): boolean {
        return this.sampleHelperService.showPublishSampleButton(sample);
    }

    public showRequestApprovalButton(sample: Sample): boolean {
        return this.sampleHelperService.showRequestApprovalButton(sample);
    }

    public showApproveSampleByReviewerButton(sample: Sample): boolean {
        return this.sampleHelperService.showApproveSampleByReviewerButton(sample);
    }

    public showApproveSampleByProcessOwnerButton(sample: Sample): boolean {
        return this.sampleHelperService.showApproveSampleByProcessOwnerButton(sample);
    }

    public showSetInProductionButton(sample: Sample): boolean {
        return this.sampleHelperService.showSetInProductionButton(sample);
    }

    public showSetActiveByProducerButton(sample: Sample): boolean {
        return this.sampleHelperService.showSetActiveByProducerButton(sample);
    }

    public showRequestChangesByReviewerButton(sample: Sample): boolean {
        return this.sampleHelperService.showRequestChangesButton(sample);
    }

    public showRequestMasterSampleButton(sample: Sample): boolean {
        return this.sampleHelperService.showRequestMasterSampleButton(sample);
    }

    public openCreateSampleDialog(): void {
        this.sampleToCopy = null;
        this.displayCreateSampleDialog = true;
    }

    public onSearchInputChange() {
        this.searchSubject.next(this.search);

        const storedFilters = sessionStorage.getItem('sample-filters');
        const parsed = storedFilters ? JSON.parse(storedFilters) : {};
        sessionStorage.setItem('sample-filters', JSON.stringify({
            ...parsed,
            search: this.search
        }));
    }

    public exportSamples() {
        this.exportService.exportSamples().then(() => {
            this.messageService.add({
                severity: 'success',
                summary: 'Exported',
                detail: 'Samples exported successfully',
                life: 3000,
            });
        }).catch((error: any) => {
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: error.message,
                life: 3000,
            });
        });
    }

    public exportSampleLanguages() {
        this.exportService.exportSampleLanguages().then(() => {
            this.messageService.add({
                severity: 'success',
                summary: 'Exported',
                detail: 'Sample languages exported successfully',
                life: 3000,
            });
        }).catch((error: any) => {
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: error.message,
                life: 3000,
            });
        });
    }

    public canSeeVehicleModels(): boolean {
        return this.authService.canSeeVehicleModels();
    }

    public exportSampleVehicleModels() {
        this.exportService.exportSampleVehicleModels().then(() => {
            this.messageService.add({
                severity: 'success',
                summary: 'Exported',
                detail: 'Sample vehicle models exported successfully',
                life: 3000,
            });
        }).catch((error: any) => {
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: error.message,
                life: 3000,
            });
        });
    }

    public copySample(sample: Sample) {
        this.sampleToCopy = new SampleWriteDTO(sample);
        this.displayCreateSampleDialog = true;
    }

    public viewSample(sample: Sample): void {
        this.router.navigate([ '/samples/sample', sample.id ]);
    }

    public getDetailNames(sample: Sample): string {
        return sample.categoryDetails.map(detail => detail.name).join(', ');
    }

    public toggleOnHold(sample: Sample) {
        this.selectedSampleObject = sample;
        this.showOnHoldDialog = true;
    }

    public openRowActionsMenu(event: Event, menu: Menu, sample: Sample): void {
        this.rowActionItems = this.getRowActionItems(sample);
        menu.toggle(event);
    }

    private getRowActionItems(sample: Sample): MenuItem[] {
        const isProducer = this.authService.hasRole(Role.SAMPLE_STUDIO_PRODUCER);
        const isMaintainer = this.authService.hasRole(Role.SAMPLE_STUDIO_MAINTAINER);
        const isProcessOwner = this.authService.hasRole(Role.SAMPLE_STUDIO_PROCESS_OWNER);
        const hasToggleOnHoldRole = this.authService.hasAnyRole([
            Role.SAMPLE_STUDIO_ADMIN,
            Role.SAMPLE_STUDIO_MAINTAINER,
            Role.SAMPLE_STUDIO_PRODUCER,
            Role.SAMPLE_STUDIO_PROCESS_OWNER
        ]);
        const canToggleOnHold = hasToggleOnHoldRole &&
            sample.state !== SampleState.ARCHIVED &&
            sample.state !== SampleState.ACTIVE;
        const canResendLatestMail = sample.state !== SampleState.CREATED && !sample.onHold;
        const deleteDisabled = sample.state === SampleState.ACTIVE ||
            sample.state === SampleState.ARCHIVED;
        const items: MenuItem[] = [
            {
                label: 'View sample',
                icon: 'pi pi-eye',
                command: () => this.viewSample(sample)
            }
        ];

        if (canToggleOnHold) {
            items.push({
                label: 'On hold',
                icon: 'pi pi-stop-circle',
                command: () => this.toggleOnHold(sample)
            });
        }

        if (canResendLatestMail) {
            items.push({
                label: 'Resend latest mail',
                icon: 'pi pi-envelope',
                command: () => this.resendLatestMail(sample)
            });
        }

        if (isProducer && this.showRequestMasterSampleButton(sample) && !sample.onHold) {
            items.push({
                label: 'Request master sample',
                icon: 'pi pi-database',
                command: () => this.requestMasterSample(sample)
            });
        }

        if (isProducer && this.showRequestChangesByReviewerButton(sample) && !sample.onHold) {
            items.push({
                label: 'Request changes',
                icon: 'pi pi-times',
                command: () => this.requestChangesByReviewer(sample)
            });
        }

        if (isMaintainer && this.showPublishSampleButton(sample) && !sample.onHold) {
            items.push({
                label: 'Publish sample',
                icon: 'pi pi-arrow-right',
                command: () => this.publishSample(sample)
            });
        }

        if (isProducer && this.showRequestApprovalButton(sample) && !sample.onHold) {
            items.push({
                label: 'Request approval',
                icon: 'pi pi-question-circle',
                command: () => this.requestApproval(sample)
            });
        }

        if (isProducer && this.showApproveSampleByReviewerButton(sample) && !sample.onHold) {
            items.push({
                label: 'Approve as producer',
                icon: 'pi pi-check',
                command: () => this.approveSampleByReviewer(sample)
            });
        }

        if (isProcessOwner && this.showSetInProductionButton(sample) && !sample.onHold) {
            items.push({
                label: 'Set in production',
                icon: 'pi pi-cog',
                command: () => this.setInProduction(sample)
            });
        }

        if (isProcessOwner && this.showApproveSampleByProcessOwnerButton(sample) && !sample.onHold) {
            items.push({
                label: 'Set active',
                icon: 'pi pi-verified',
                command: () => this.approveSampleByProcessOwner(sample)
            });
        }

        if (isProducer && !isProcessOwner && this.showSetActiveByProducerButton(sample) && !sample.onHold) {
            items.push({
                label: 'Set active',
                icon: 'pi pi-verified',
                command: () => this.approveSampleByProcessOwner(sample)
            });
        }

        if (isMaintainer) {
            items.push({ separator: true });
            items.push(
                {
                    label: 'Edit sample',
                    icon: 'pi pi-pencil',
                    command: () => this.editSample(sample)
                },
                {
                    label: 'Delete sample',
                    icon: 'pi pi-trash',
                    disabled: deleteDisabled,
                    command: () => this.deleteSample(sample)
                },
                {
                    label: 'Copy sample',
                    icon: 'pi pi-copy',
                    command: () => this.copySample(sample)
                }
            );
        }

        return items;
    }

    private restoreFiltersFromSession(): void {
        const storedFilters = sessionStorage.getItem('sample-filters');
        if (storedFilters) {
            try {
                const filters: SampleFilters = JSON.parse(storedFilters);

                this.selectedFieldsOfUse = filters.fieldOfUse || [];
                this.selectedSampleTypes = filters.sampleType || [];
                this.selectedSampleStates = filters.sampleState || [];
                this.selectedSampleCategories = filters.sampleCategory || [];
                this.selectedSampleDetails = filters.sampleDetails || [];
                this.selectedFormats = filters.format || [];
                this.search = filters.search || '';

            } catch (e) {
                console.warn('Could not restore sample filters from sessionStorage:', e);
            }
        }
    }

    protected readonly Detail = Detail;
    protected readonly SampleState = SampleState;

    public onCustomExport($event: any) {
        this.displayCustomExportDialog = false;
        this.messageService.add(
            {
                severity: 'info',
                summary: 'Exporting',
                detail: 'Starting custom sample export, this can take a couple of seconds...',
                life: 10000,
            }
        );
        this.exportService.customSampleExport($event.filters, $event.hideEmptyColumns).then(() => {
            this.messageService.add({
                severity: 'success',
                summary: 'Exported',
                detail: 'Custom sample export completed successfully',
                life: 3000,
            });
        }).catch((error: any) => {
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: error.message,
                life: 3000,
            });
        });
    }
}

interface SampleFilters {
    fieldOfUse: FieldOfUse[];
    sampleType: SampleType[];
    sampleState: SampleState[];
    sampleCategory: SampleCategory[];
    sampleDetails: Detail[];
    format: SampleFormat[];
    search: string;

}
